import { LANG_TEXT } from '../constants/language';
import { PreorderRuleState, TimeUnit } from '../pages/PreoderRules/PreorderRules.types';
import { IPreorderRules, ITimeUnitValue, ITimeUnitValueWithoutPicker } from '../pages/Settings/settings.types';

type ApiPreorderRules = {
  acceptance?: {
    since?: ITimeUnitValue;
    until?: ITimeUnitValue;
  };
  time_slot?: ITimeUnitValueWithoutPicker;
  order_deliberable?: {
    since?: ITimeUnitValueWithoutPicker;
    until?: ITimeUnitValueWithoutPicker;
  };
};

export type PreorderRuleValidationError = 'acceptanceSince' | 'acceptanceUntil' | 'orderDeliverableUntil';

export const convertDurationToMinutes = (value: number, type: string): number => {
  if (type === 'minutes' || type === 'minute' || type === 'mins') return value;
  if (type === 'hours' || type === 'hour') return value * 60;
  if (type === 'days' || type === 'day') return value * 24 * 60;
  return value;
};

const typeToUnit = (type: string | undefined, fallback: TimeUnit = 'day'): TimeUnit => {
  if (type === 'minutes' || type === 'minute' || type === 'mins') return 'minute';
  if (type === 'hours' || type === 'hour') return 'hour';
  if (type === 'days' || type === 'day') return 'day';
  return fallback;
};

export const convertAPIToPreorderRuleState = (rules: ApiPreorderRules | undefined, fallback: PreorderRuleState): PreorderRuleState => {
  if (!rules) return fallback;

  return {
    acceptanceSince: {
      value: rules.acceptance?.since?.time ?? fallback.acceptanceSince.value,
      unit: typeToUnit(rules.acceptance?.since?.type, fallback.acceptanceSince.unit),
      pickerValue: rules.acceptance?.since?.at || fallback.acceptanceSince.pickerValue,
    },
    acceptanceUntil: {
      value: rules.acceptance?.until?.time ?? fallback.acceptanceUntil.value,
      unit: typeToUnit(rules.acceptance?.until?.type, fallback.acceptanceUntil.unit),
      pickerValue: rules.acceptance?.until?.at || fallback.acceptanceUntil.pickerValue,
    },
    timeSlotsSince: {
      value: rules.time_slot?.time ?? fallback.timeSlotsSince.value,
      unit: typeToUnit(rules.time_slot?.type, fallback.timeSlotsSince.unit),
    },
    orderDeliverableUntil: {
      value: rules.order_deliberable?.until?.time ?? fallback.orderDeliverableUntil.value,
      unit: typeToUnit(rules.order_deliberable?.until?.type, fallback.orderDeliverableUntil.unit),
    },
  };
};

export const getStadiumPreorderRuleState = (stadiumData: any, fallback: PreorderRuleState): PreorderRuleState => {
  const rules = stadiumData?.pre_order_rules?.preorder_rules || stadiumData?.preorder_rules;

  return convertAPIToPreorderRuleState(rules, fallback);
};

const timeStringToMinutes = (time?: string): number => {
  if (!time) return 0;

  const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;

  return hours * 60 + minutes;
};

const acceptancePoint = (value: { time: number; type: string; at?: string }): number => {
  return -convertDurationToMinutes(value.time || 0, value.type) + timeStringToMinutes(value.at);
};

const ruleAcceptanceSincePoint = (rule: PreorderRuleState): number => {
  return acceptancePoint({
    time: rule.acceptanceSince.value,
    type: rule.acceptanceSince.unit,
    at: rule.acceptanceSince.pickerValue,
  });
};

const ruleAcceptanceUntilPoint = (rule: PreorderRuleState): number => {
  return acceptancePoint({
    time: rule.acceptanceUntil.value,
    type: rule.acceptanceUntil.unit,
    at: rule.acceptanceUntil.pickerValue,
  });
};

const ruleOrderDeliverableUntilMinutes = (rule: PreorderRuleState): number => {
  return convertDurationToMinutes(rule.orderDeliverableUntil.value, rule.orderDeliverableUntil.unit);
};

export const validatePreorderRuleAgainstMaster = (rule: PreorderRuleState, masterRule?: PreorderRuleState): PreorderRuleValidationError | null => {
  if (!masterRule) return null;

  if (ruleAcceptanceSincePoint(rule) < ruleAcceptanceSincePoint(masterRule)) {
    return 'acceptanceSince';
  }

  if (ruleAcceptanceUntilPoint(rule) > ruleAcceptanceUntilPoint(masterRule)) {
    return 'acceptanceUntil';
  }

  if (ruleOrderDeliverableUntilMinutes(rule) > ruleOrderDeliverableUntilMinutes(masterRule)) {
    return 'orderDeliverableUntil';
  }

  return null;
};

export const getPreorderRuleValidationMessage = (error: PreorderRuleValidationError): string => {
  if (error === 'acceptanceSince') {
    return LANG_TEXT.PRE_ORDER_ACCEPTANCE_SINCE_EXCEEDS_ADMIN_RULE;
  }

  if (error === 'acceptanceUntil') {
    return LANG_TEXT.PRE_ORDER_ACCEPTANCE_UNTIL_EXCEEDS_ADMIN_RULE;
  }

  return LANG_TEXT.ORDERS_DELIVERABLE_EXCEEDS_ADMIN_RULE;
};

export const getFoodTruckPreorderRuleConstraintUpdates = (foodTruckRules: ApiPreorderRules | undefined, adminRules: IPreorderRules): Record<string, any> => {
  if (!foodTruckRules) return {};

  const updates: Record<string, any> = {};

  if (foodTruckRules.acceptance?.since && acceptancePoint(foodTruckRules.acceptance.since) < acceptancePoint(adminRules.acceptance.since)) {
    updates['preorder_rules.acceptance.since'] = adminRules.acceptance.since;
  }

  if (foodTruckRules.acceptance?.until && acceptancePoint(foodTruckRules.acceptance.until) > acceptancePoint(adminRules.acceptance.until)) {
    updates['preorder_rules.acceptance.until'] = adminRules.acceptance.until;
  }

  if (
    foodTruckRules.order_deliberable?.until &&
    convertDurationToMinutes(foodTruckRules.order_deliberable.until.time || 0, foodTruckRules.order_deliberable.until.type) >
      convertDurationToMinutes(adminRules.order_deliberable.until.time || 0, adminRules.order_deliberable.until.type)
  ) {
    updates['preorder_rules.order_deliberable.until'] = adminRules.order_deliberable.until;
  }

  return updates;
};
